import React, { useEffect, useState } from "react";
import { Layout, Menu, Popover } from "antd";
import {
  DashboardOutlined,
  BarChartOutlined,
  MenuOutlined,
  CloseOutlined,
  LogoutOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
  ShopOutlined,
  TeamOutlined,
  DownOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation, Navigate, Outlet } from "react-router-dom";
import { isConsoleUser } from "../constants/roles";
import "../assets/scss/Admin.scss";
import "../assets/scss/style.scss";
import "../styles/common.css";
import SuperadminLogo from "../assets/icons/SuperAdminIcon";
import ProfileImage from "../assets/images/default_profile.jpg";
import ChangePasswordModal from "../modals/ChangePasswordModal";

const { Header, Sider, Content } = Layout;

const MENU_ITEMS = [
  { key: "/admin/dashboard", label: "Dashboard", icon: <DashboardOutlined /> },
  { key: "/admin/reports", label: "Analytics", icon: <BarChartOutlined /> },
  { key: "/admin/plan-management", label: "Plans", icon: <FileTextOutlined /> },
  { key: "/admin/company-health", label: "Health", icon: <SafetyCertificateOutlined /> },
  { key: "/admin/users", label: "Users", icon: <TeamOutlined /> },
];

// Console users only get their own companies and the health pages for them.
const USER_MENU_ITEMS = [
  { key: "/admin/dashboard", label: "My Companies", icon: <ShopOutlined /> },
  { key: "/admin/company-health", label: "Health", icon: <SafetyCertificateOutlined /> },
];
const USER_PATHS = USER_MENU_ITEMS.map((item) => item.key);

const FLUSH_PAGES = ["/admin/plan-management"];

const readProfile = () => {
  try {
    return JSON.parse(localStorage.getItem("userData")) || null;
  } catch {
    return null;
  }
};

const AdminLayout = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [profile] = useState(readProfile);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const consoleUser = isConsoleUser();
  const blockedForUser = consoleUser && !USER_PATHS.some((path) => pathname.startsWith(path));

  const displayName =
    [profile?.firstname, profile?.lastname].filter(Boolean).join(" ") || "User";
  const selectedMenuKey =
    ["/admin/plan-management", "/admin/company-health"].find((key) => pathname.startsWith(key)) || pathname;
  const isFlushPage = FLUSH_PAGES.includes(pathname);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 767;
      setIsMobile(mobile);
      if (!mobile) setMobileMenuVisible(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", "light");
    document.body.classList.add("admin-user");
    const timer = setTimeout(() => document.body.classList.add("load-content"), 250);
    return () => {
      clearTimeout(timer);
      document.body.classList.remove("admin-user", "load-content");
    };
  }, []);

  const handleMenuClick = ({ key }) => {
    navigate(key);
    if (isMobile) setMobileMenuVisible(false);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  const userMenu = (
    <div className="um-card">
      <div className="um-card-header">
        <img src={profile?.profilePic || ProfileImage} className="um-card-avatar" alt="User" />
        <div className="um-card-identity">
          <div className="um-card-name" title={displayName}>{displayName}</div>
          <div className="um-card-role">{consoleUser ? "User" : "Super Admin"}</div>
          {profile?.email && <div className="um-card-email" title={profile.email}>{profile.email}</div>}
        </div>
      </div>
      <div className="um-divider" />
      <button
        type="button"
        className="um-menu-item"
        onClick={() => {
          setUserMenuOpen(false);
          setPasswordModalOpen(true);
        }}
      >
        <LockOutlined /> Change Password
      </button>
      <button type="button" className="um-menu-item um-menu-item--danger" onClick={handleLogout}>
        <LogoutOutlined /> Logout
      </button>
    </div>
  );

  return (
    <Layout className="app-wrapper admin" style={{ minHeight: "100dvh" }}>
      <Sider
        className="sidebar"
        width={136}
        style={{
          position: isMobile ? "fixed" : "relative",
          left: isMobile && !mobileMenuVisible ? "-220px" : "0",
          zIndex: isMobile ? 1000 : 1,
          height: isMobile ? "100dvh" : "auto",
          transition: isMobile ? "left 0.25s ease" : undefined,
        }}
      >
        <div className="sidebar-header" style={{ position: "relative" }}>
          <div className="sidebar-logo">
            <SuperadminLogo />
          </div>
          {isMobile && (
            <button
              type="button"
              className="sidebar-close-btn"
              aria-label="Close menu"
              onClick={() => setMobileMenuVisible(false)}
            >
              <CloseOutlined />
            </button>
          )}
        </div>
        <Menu
          className="sidebar-menu"
          selectedKeys={[selectedMenuKey]}
          onClick={handleMenuClick}
          items={consoleUser ? USER_MENU_ITEMS : MENU_ITEMS}
        />
      </Sider>

      {isMobile && mobileMenuVisible && (
        <div
          style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0, 0, 0, 0.5)", backdropFilter: "blur(1px)", zIndex: 999 }}
          onClick={() => setMobileMenuVisible(false)}
        />
      )}

      <Layout>
        <Header className="app-topbar">
          {isMobile && (
            <button
              type="button"
              className="app-topbar__menu-btn"
              aria-label="Open menu"
              onClick={() => setMobileMenuVisible(true)}
            >
              <MenuOutlined />
            </button>
          )}
          <h2 className="app-topbar__title">WeekMate Super Admin</h2>
          <div className="app-topbar__actions">
            <Popover
              overlayClassName="user-menu-popover"
              placement="bottomRight"
              content={userMenu}
              trigger="click"
              open={userMenuOpen}
              onOpenChange={setUserMenuOpen}
            >
              <button
                type="button"
                className={`um-pill${userMenuOpen ? " um-pill-open" : ""}`}
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
              >
                <img src={profile?.profilePic || ProfileImage} className="um-pill-avatar" alt="User" />
                <span className="um-pill-name">{displayName}</span>
                <DownOutlined className="um-pill-chevron" />
              </button>
            </Popover>
          </div>
        </Header>

        <Content>
          <div
            className={`content-wrapper${isFlushPage ? " content-wrapper--flush" : ""}`}
            style={{ maxHeight: "calc(100dvh - 66px)", overflowY: "auto" }}
          >
            {blockedForUser ? <Navigate to="/admin/dashboard" replace /> : <Outlet />}
          </div>
        </Content>
      </Layout>
      <ChangePasswordModal open={passwordModalOpen} onClose={() => setPasswordModalOpen(false)} />
    </Layout>
  );
};

export default AdminLayout;
